import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { fetchPlans, startCheckout, applyCoupon } from "../../store/thunks/subscriptionThunk";
import { clearCoupon } from "../../store/slices/subscriptionSlice";
import { useAuthContext } from "../../features/authentication";
import Header from "../../components/common/Header";
import Footer from "../../components/common/Footer";
import { colors } from "../../utils/Colors";
import RoutesPath from "../../utils/Paths";

const c = colors.color;

const PLAN_ICONS = ["🚀", "⚡", "💎", "👑", "🔥", "🌟"];
const PLAN_GRADIENTS = [
  `linear-gradient(135deg, ${c.secondary}, #00a67e)`,
  `linear-gradient(135deg, #446EE7, #6a4fdb)`,
  `linear-gradient(135deg, #f1b44c, #f46a6a)`,
  `linear-gradient(135deg, #7b2ff7, #f107a3)`,
];

const SubscriptionPlansPublic = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { plans, checkout, coupon } = useSelector((s) => s.subscription);
  const { isAuthenticated } = useAuthContext();

  const [couponInput, setCouponInput] = useState("");
  const [selectedPlanForCoupon, setSelectedPlanForCoupon] = useState(null);

  useEffect(() => {
    dispatch(fetchPlans());
    return () => { dispatch(clearCoupon()); };
  }, [dispatch]);

  const handleApplyCoupon = (plan) => {
    if (!couponInput.trim()) return;
    setSelectedPlanForCoupon(plan._id);
    dispatch(applyCoupon({ couponCode: couponInput.trim(), subscriptionPlanId: plan._id }));
  };

  const handleClearCoupon = () => {
    setCouponInput("");
    setSelectedPlanForCoupon(null);
    dispatch(clearCoupon());
  };

  const handleCheckout = (plan) => {
    if (!isAuthenticated) {
      navigate(RoutesPath.signIn);
      return;
    }
    const couponApplied =
      isAuthenticated && coupon.data?.valid && selectedPlanForCoupon === plan._id
        ? couponInput.trim()
        : null;
    dispatch(startCheckout({ planId: plan._id, couponCode: couponApplied }));
  };

  return (
    <>
      <Header />

      <style>{`
        @media (max-width: 1024px) {
          .subscription-plans-grid {
            grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
            max-width: 640px !important;
          }
        }
        @media (max-width: 560px) {
          .subscription-plans-grid {
            grid-template-columns: 1fr !important;
            max-width: 360px !important;
          }
        }
      `}</style>

      <div style={{ background: "#f7fffe", minHeight: "100vh" }}>
        <div
          style={{
            background: `linear-gradient(135deg, ${c.secondary}18, #00a67e10, #fff)`,
            borderBottom: `1px solid ${c.lightGrey}`,
            padding: "60px 0 50px",
            textAlign: "center",
          }}
        >
          <div className="container">
            <h1
              style={{
                fontSize: "clamp(28px, 5vw, 46px)",
                fontWeight: 800,
                color: c.primary,
                lineHeight: 1.2,
                marginBottom: 16,
              }}
            >
              Subscription Plans
            </h1>

            <p style={{ fontSize: 17, color: c.darkGrey, maxWidth: 640, margin: "0 auto 12px", lineHeight: 1.6 }}>
              Post jobs and find the right candidates.{" "}
              <strong style={{ color: c.primary }}>Your first job post is always free.</strong>
            </p>
          </div>
        </div>

        <div className="container" style={{ padding: "48px 16px 80px" }}>
          {plans.loading ? (
            <div style={{ textAlign: "center", padding: "80px 0" }}>
              <div
                style={{
                  width: 48,
                  height: 48,
                  border: `4px solid ${c.lightGrey}`,
                  borderTop: `4px solid ${c.secondary}`,
                  borderRadius: "50%",
                  margin: "0 auto 16px",
                  animation: "spin 0.8s linear infinite",
                }}
              />
              <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
              <p style={{ color: c.darkGrey, fontSize: 15 }}>Loading plans...</p>
            </div>
          ) : plans.data.length === 0 ? (
            <div style={{ textAlign: "center", padding: "80px 0" }}>
              <div style={{ fontSize: 56, marginBottom: 16 }}>📋</div>
              <h4 style={{ color: c.primary }}>No plans available yet</h4>
              <p style={{ color: c.darkGrey }}>Check back soon!</p>
            </div>
          ) : (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: `repeat(${plans.data.length}, minmax(0, 1fr))`,
                gap: 24,
                maxWidth: Math.min(plans.data.length * 300, 1400),
                margin: "0 auto",
              }}
              className="subscription-plans-grid"
            >
              {plans.data.map((plan, idx) => {
                const gradient = PLAN_GRADIENTS[idx % PLAN_GRADIENTS.length];
                const icon = PLAN_ICONS[idx % PLAN_ICONS.length];
                const isFreePlan = !plan.price || Number(plan.price) === 0;

                const features = [
                  isFreePlan
                    ? "1st job free"
                    : `${plan.jobsLimit} additional job${plan.jobsLimit !== 1 ? "s" : ""}`,
                  "Access to all candidates",
                  "Email notifications",
                  "Candidate messaging",
                  ...(plan.jobsLimit >= 10 ? ["Priority listing"] : []),
                  ...(plan.jobsLimit >= 20 ? ["Dedicated support"] : []),
                ];

                return (
                  <div
                    key={plan._id}
                    style={{
                      background: "#fff",
                      borderRadius: 20,
                      overflow: "hidden",
                      boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                      position: "relative",
                      transition: "transform 0.25s, box-shadow 0.25s",
                    }}
                  >
                    <div style={{ background: gradient, padding: "28px 28px 24px" }}>
                      <div style={{ fontSize: 36, marginBottom: 10 }}>{icon}</div>
                      <h3 style={{ color: "#fff", fontWeight: 800, fontSize: 22, margin: 0 }}>{plan.name}</h3>
                      {plan.description && (
                        <p style={{ color: "rgba(255,255,255,0.8)", fontSize: 13, margin: "6px 0 0", lineHeight: 1.4 }}>
                          {plan.description}
                        </p>
                      )}
                      <div style={{ display: "flex", alignItems: "flex-end", gap: 4, marginTop: 18 }}>
                        <span style={{ fontSize: 42, fontWeight: 900, color: "#fff", lineHeight: 1 }}>${plan.price}</span>
                        <span style={{ color: "rgba(255,255,255,0.75)", fontSize: 15, marginBottom: 4 }}>/ month</span>
                      </div>
                    </div>

                    <div style={{ padding: "24px 28px" }}>
                      <ul style={{ listStyle: "none", padding: 0, margin: "0 0 24px" }}>
                        {features.map((f) => (
                          <li
                            key={f}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 10,
                              padding: "8px 0",
                              borderBottom: `1px solid ${c.lightGrey}55`,
                              fontSize: 14,
                              color: c.primary,
                            }}
                          >
                            <span style={{ width: 22, height: 22, borderRadius: "50%", background: `${c.secondary}18`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, flexShrink: 0, color: c.secondary, fontWeight: 700 }}>✓</span>
                            {f}
                          </li>
                        ))}
                      </ul>

                      {/* ── Coupon Section (logged-in users only) ── */}
                      {isAuthenticated && (
                        <div style={{ marginBottom: 14 }}>
                          {!(coupon.data?.valid && selectedPlanForCoupon === plan._id) && (
                            <div style={{ display: "flex", gap: 8 }}>
                              <input
                                value={selectedPlanForCoupon === plan._id || !selectedPlanForCoupon ? couponInput : ""}
                                onChange={(e) => {
                                  setCouponInput(e.target.value.toUpperCase());
                                  if (selectedPlanForCoupon !== plan._id) {
                                    dispatch(clearCoupon());
                                    setSelectedPlanForCoupon(null);
                                  }
                                }}
                                onFocus={() => {
                                  if (selectedPlanForCoupon !== plan._id) {
                                    dispatch(clearCoupon());
                                    setSelectedPlanForCoupon(null);
                                  }
                                }}
                                placeholder="Coupon code"
                                style={{
                                  flex: 1,
                                  padding: "9px 12px",
                                  borderRadius: 10,
                                  border: `1.5px solid ${coupon.error && selectedPlanForCoupon === plan._id ? "#f46a6a" : c.lightGrey}`,
                                  fontSize: 13,
                                  fontWeight: 600,
                                  letterSpacing: "0.5px",
                                  outline: "none",
                                  color: c.primary,
                                }}
                              />
                              <button
                                onClick={() => handleApplyCoupon(plan)}
                                disabled={coupon.loading && selectedPlanForCoupon === plan._id}
                                style={{
                                  padding: "9px 14px",
                                  borderRadius: 10,
                                  border: `1.5px solid ${c.secondary}`,
                                  background: "transparent",
                                  color: c.secondary,
                                  fontWeight: 700,
                                  fontSize: 13,
                                  cursor: "pointer",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {coupon.loading && selectedPlanForCoupon === plan._id ? "..." : "Apply"}
                              </button>
                            </div>
                          )}

                          {coupon.error && selectedPlanForCoupon === plan._id && (
                            <p style={{ color: "#f46a6a", fontSize: 12, margin: "6px 0 0", fontWeight: 500 }}>
                              ✕ {coupon.error}
                            </p>
                          )}

                          {coupon.data?.valid && selectedPlanForCoupon === plan._id && (
                            <div style={{ background: `${c.secondary}0d`, border: `1.5px solid ${c.secondary}40`, borderRadius: 10, padding: "12px 14px", fontSize: 13 }}>
                              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                                <span style={{ color: c.darkGrey }}>Original Price</span>
                                <span style={{ color: c.primary, fontWeight: 600 }}>${coupon.data.originalPrice}</span>
                              </div>
                              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                                <span style={{ color: "#27ae60" }}>🏷️ Coupon ({coupon.data.discountValue}% off)</span>
                                <span style={{ color: "#27ae60", fontWeight: 700 }}>−${coupon.data.discountAmount}</span>
                              </div>
                              <div style={{ display: "flex", justifyContent: "space-between", paddingTop: 6, borderTop: `1px solid ${c.secondary}30`, marginTop: 4 }}>
                                <span style={{ color: c.primary, fontWeight: 700 }}>Final Price</span>
                                <span style={{ color: c.secondary, fontWeight: 800, fontSize: 15 }}>${coupon.data.finalPrice}</span>
                              </div>
                              <button onClick={handleClearCoupon} style={{ marginTop: 8, background: "none", border: "none", color: "#f46a6a", fontSize: 12, cursor: "pointer", padding: 0, fontWeight: 500 }}>
                                ✕ Remove coupon
                              </button>
                            </div>
                          )}
                        </div>
                      )}

                      <button
                        disabled={checkout.loading}
                        onClick={() => handleCheckout(plan)}
                        style={{
                          width: "100%",
                          padding: "13px",
                          borderRadius: 12,
                          border: "none",
                          background: checkout.loading ? c.lightGrey : gradient,
                          color: "#fff",
                          fontWeight: 700,
                          fontSize: 15,
                          cursor: checkout.loading ? "not-allowed" : "pointer",
                        }}
                      >
                        {checkout.loading ? "Redirecting..." : "Get Started →"}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <Footer />
    </>
  );
};

export default SubscriptionPlansPublic;
